// Import necessary React hooks and Material-UI components
import React, { useState } from "react";
import "./chooseProgram.css";
import {
  Box,
  Grid,
  Typography,
  Button,
  Modal,
  Backdrop,
  IconButton,
} from "@mui/material";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import CloseIcon from "@mui/icons-material/Close";
import { websiteImages } from "../../../config/config";
import { fitnessHeroImages } from "../../../config/config";

// Define an array of image objects with source and alt text
const images = [
  { src: fitnessHeroImages.fitnesssHero01, alt: "Fitness Hero 1" },
  { src: fitnessHeroImages.fitnesssHero02, alt: "Fitness Hero 2" },
  { src: fitnessHeroImages.fitnesssHero03, alt: "Fitness Hero 3" },
  { src: fitnessHeroImages.fitnesssHero04, alt: "Fitness Hero 4" },
];

// ChooseProgram component to explain how the program works and display images
const ChooseProgram = () => {
  // State to manage the selected image, gallery visibility, and current image index
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Function to open the gallery modal with the selected image
  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(images[index]);
    setGalleryOpen(true);
  };

  // Function to close the gallery modal
  const closeGallery = () => {
    setGalleryOpen(false);
    setSelectedImage(null);
  };

  // Function to navigate to the next image in the gallery
  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(images[nextIndex]);
  };

  // Function to navigate to the previous image in the gallery
  const prevImage = () => {
    const prevIndex =
      currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(images[prevIndex]);
  };

  // Effect to handle keyboard navigation for the gallery modal
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (!galleryOpen) return;

      if (event.key === "ArrowLeft") {
        prevImage();
      } else if (event.key === "ArrowRight") {
        nextImage();
      } else if (event.key === "Escape") {
        closeGallery();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [galleryOpen, currentImageIndex]);

  return (
    // Main container for the ChooseProgram section with relative positioning for overlay
    <div className="section-06" style={{ position: "relative" }}>
      {/* Overlay image in the corner */}
      <img
        src={websiteImages.vectorGoalImage}
        alt="corner-overlay"
        className="corner-overlay"
      />
      <Box sx={{ backgroundColor: "#111111", padding: "80px 0px" }}>
        <Box
          className="container"
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Overline text */}
          <Typography
            variant="overline"
            sx={{
              color: "#fff",
              fontSize: {
                xs: "8px",
                sm: "9px",
                md: "10px",
                lg: "10px",
              },
              letterSpacing: {
                xs: "6px",
                sm: "8px",
                md: "10px",
              },
              fontWeight: "700",
              textAlign: "center",
              mb: { xs: 1, sm: 1.5 },
            }}
          >
            How the Program Works
          </Typography>

          {/* Main heading */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mt: 1,
              maxWidth: {
                xs: "100%",
                sm: "90%",
                md: "80%",
                lg: "900px",
              },
              textTransform: "uppercase",
              color: "#fff",
              fontSize: {
                xs: "18px",
                sm: "24px",
                md: "30px",
                lg: "35px",
                xl: "40px",
              },
              lineHeight: {
                xs: 1.2,
                sm: 1.2,
                md: 1.3,
                lg: 1.3,
              },
              textAlign: "center",
              mb: { xs: 2, sm: 3 },
            }}
          >
            Recomp Plus : Designed to Evolve With You
          </Typography>

          {/* Content section explaining the program steps */}
          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
              color: "#fff",
              maxWidth: {
                xs: "100%",
                sm: "85%",
                md: "75%",
                lg: "60%",
                xl: "50%",
              },
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              mx: "auto",
              textAlign: { xs: "left", sm: "center" },
              px: { xs: 1, sm: 2 },
            }}
          >
            {/* Section 1: Get Your Blueprint */}
            <Typography
              component="p"
              sx={{
                fontWeight: "bold",
                fontSize: {
                  xs: "16px",
                  sm: "18px",
                  md: "19px",
                  lg: "20px",
                },
                paddingTop: { xs: "8px", sm: "10px" },
                mb: { xs: 1, sm: 1.5 },
                color: "#fff",
              }}
            >
              Get Your Blueprint
            </Typography>

            <Typography
              component="li"
              sx={{
                color: "#fff",
                fontSize: {
                  xs: "12px",
                  sm: "13px",
                  md: "14px",
                  lg: "14px",
                },
                listStyleType: "none",
                lineHeight: {
                  xs: 1.4,
                  sm: 1.5,
                  md: 1.6,
                },
                ml: { xs: 2, sm: 3 },
                color: "#fff",
                paddingBottom: { xs: "6px", sm: "8px", md: "10px" },
                fontWeight: { xs: 500, md: 500 },
                position: "relative",
                paddingLeft: { xs: "20px", sm: "24px", md: "28px" },
                "&:before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: { xs: "6px", sm: "8px", md: "10px" },
                  width: { xs: "4px", sm: "5px", md: "6px" },
                  height: { xs: "4px", sm: "5px", md: "6px" },
                  backgroundColor: "#00ffff",
                  borderRadius: "50%",
                },
              }}
            >
              You'll start with a structured strength + hypertrophy plan based
              on your training days and experience level — all aligned to your
              physique goals.
            </Typography>

            {/* Section 2: Follow the System */}
            <Typography
              component="p"
              sx={{
                fontWeight: "bold",
                fontSize: {
                  xs: "16px",
                  sm: "18px",
                  md: "19px",
                  lg: "20px",
                },
                paddingTop: { xs: "12px", sm: "15px" },
                mb: { xs: 1, sm: 1.5 },
                color: "#fff",
              }}
            >
              Follow the System
            </Typography>

            <Typography
              component="li"
              sx={{
                color: "#fff",
                fontSize: {
                  xs: "12px",
                  sm: "13px",
                  md: "14px",
                  lg: "14px",
                },
                listStyleType: "none",
                lineHeight: {
                  xs: 1.4,
                  sm: 1.5,
                  md: 1.6,
                },
                ml: { xs: 2, sm: 3 },
                color: "#fff",
                paddingBottom: { xs: "6px", sm: "8px", md: "10px" },
                fontWeight: { xs: 500, md: 500 },
                position: "relative",
                paddingLeft: { xs: "20px", sm: "24px", md: "28px" },
                "&:before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: { xs: "6px", sm: "8px", md: "10px" },
                  width: { xs: "4px", sm: "5px", md: "6px" },
                  height: { xs: "4px", sm: "5px", md: "6px" },
                  backgroundColor: "#00ffff",
                  borderRadius: "50%",
                },
              }}
            >
              We combine intelligent training, adaptive nutrition, and regular
              check-ins to keep you accountable and progressing week after week.
            </Typography>

            {/* Section 3: See Real Progress */}
            <Typography
              component="p"
              sx={{
                fontWeight: "bold",
                fontSize: {
                  xs: "16px",
                  sm: "18px",
                  md: "19px",
                  lg: "20px",
                },
                paddingTop: { xs: "12px", sm: "15px" },
                mb: { xs: 1, sm: 1.5 },
                color: "#fff",
              }}
            >
              See Real Progress
            </Typography>

            <Typography
              component="li"
              sx={{
                color: "#fff",
                fontSize: {
                  xs: "12px",
                  sm: "13px",
                  md: "14px",
                  lg: "14px",
                },
                listStyleType: "none",
                lineHeight: {
                  xs: 1.4,
                  sm: 1.5,
                  md: 1.6,
                },
                ml: { xs: 2, sm: 3 },
                color: "#fff",
                paddingBottom: { xs: "6px", sm: "8px", md: "10px" },
                fontWeight: { xs: 500, md: 500 },
                position: "relative",
                paddingLeft: { xs: "20px", sm: "24px", md: "28px" },
                "&:before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: { xs: "6px", sm: "8px", md: "10px" },
                  width: { xs: "4px", sm: "5px", md: "6px" },
                  height: { xs: "4px", sm: "5px", md: "6px" },
                  backgroundColor: "#00ffff",
                  borderRadius: "50%",
                },
              }}
            >
              Your results won't disappear after 8 weeks. Recomp Plus is built
              to evolve as your body changes — whether you're cutting,
              maintaining, or building.
            </Typography>
          </Box>

          {/* Call-to-action button */}
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#00ffff",
              color: "#111111",
              fontSize: {
                xs: "11px",
                sm: "12px",
                md: "12px",
                lg: "12px",
                xl: "12px",
              },
              marginTop: {
                xs: "16px",
                sm: "20px",
                md: "24px",
                lg: "20px",
              },
              fontWeight: 700,
              padding: {
                xs: "8px 16px",
                sm: "10px 20px",
                md: "12px 24px",
                lg: "14px 28px",
                xl: "15px 30px",
              },
              borderRadius: "25px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              transition: "all 0.3s ease",
              minWidth: "auto",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#00ffff",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 198, 194, 0.3)",
              },
            }}
            onClick={() => {
              const section = document.getElementById("subscription-section");
              if (section) {
                section.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            Start My Plan
          </Button>
        </Box>

        {/* Image grid for displaying fitness images */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{
            mt: 4,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {images.map((image, index) => (
            <Grid
              item
              key={index}
              xs={6}
              sm={6}
              md={4}
              lg={4}
              xl={3}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "300px",
                height: {
                  xs: 250,
                  sm: 250,
                  md: 250,
                },
                borderRadius: "12px",
                overflow: "hidden",
                border: "2px solid #222",
                aspectRatio: "1 / 1",
              }}
            >
              <Box
                onClick={() => openGallery(index)}
                sx={{
                  width: "100%",
                  height: {
                    xs: 160,
                    sm: 200,
                    md: 240,
                  },
                  borderRadius: "12px",
                  border: "2px solid #333",
                  aspectRatio: "4 / 3",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  overflow: "hidden",
                  position: "relative",
                  "&:hover": {
                    transform: "scale(1.05)",
                    border: "2px solid #00ffff",
                    boxShadow: "0 8px 25px rgba(0, 198, 194, 0.4)",
                    zIndex: 1,
                  },
                }}
                title={`Click to view ${image.alt}`}
              >
                {/* Blurred background image for visual effect */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${image.src})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "blur(8px) brightness(0.3)",
                    zIndex: 1,
                  }}
                />

                {/* Main image displayed over the blurred background */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 2,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      transition: "all 0.3s ease",
                      position: "relative",
                      zIndex: 2,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Modal for full-screen image gallery */}
      <Modal
        open={galleryOpen}
        onClose={closeGallery}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {selectedImage && (
            <>
              {/* Button to close the modal */}
              <IconButton
                onClick={closeGallery}
                aria-label="close"
                sx={{
                  position: "absolute",
                  top: -50,
                  right: -50,
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Button to navigate to the previous image */}
              <IconButton
                onClick={prevImage}
                aria-label="previous"
                sx={{
                  position: "absolute",
                  left: -60,
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <WestIcon />
              </IconButton>

              {/* Main image in the modal */}
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />

              {/* Button to navigate to the next image */}
              <IconButton
                onClick={nextImage}
                aria-label="next"
                sx={{
                  position: "absolute",
                  right: -60,
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <EastIcon />
              </IconButton>

              {/* Image counter display */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: -50,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  fontSize: "14px",
                }}
              >
                {currentImageIndex + 1} / {images.length}
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

// Export the ChooseProgram component
export default ChooseProgram;
